import { useEffect, useMemo, useRef, useState } from "react";
import { FiArrowUp, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiSearch, FiX } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getGuestUsers, getRegisteredUsers } from "../../../../../api/adminMarketingUsers";
import filterIcon from "../../../../../assets/icons/admin-filter.svg";
import guestUsersIcon from "../../../../../assets/icons/admin-nav-guest.svg";
import registeredUsersIcon from "../../../../../assets/icons/admin-nav-registered.svg";
import pushChannelIcon from "../../../../../assets/icons/admin-push-notification.svg";
import { ComponentLoader } from "../../../../../components/loaders";
import { ADMIN_PERMISSIONS, useAdminAuth } from "../../../../../providers/adminAuthentication.provider";
import { getAdminBroadcastSelection, getAdminDirectorySelection, saveAdminBroadcastSelection, saveAdminDirectorySelection } from "../../../../../utils/adminBroadcastSelection";
import { DashboardHeader } from "../../dashboards/dashboard-components";
import { buyerInitials } from "../marketing-buyers.data";
import { SelectionConfirmation } from "../selection-confirmation";

const registeredUserSegments = [
  { value: "all", label: "All registered users" },
  { value: "frequent_buyers", label: "Frequent buyers" },
  { value: "inactive_buyers", label: "Inactive buyers" },
  { value: "new_users", label: "New users" },
  { value: "cancelled_orders", label: "Cancelled orders" },
];

const guestUserSegments = [
  { value: "all", label: "All guest users" },
  { value: "frequent_buyers", label: "Frequent buyers" },
  { value: "inactive_buyers", label: "Inactive buyers" },
  { value: "new_guests", label: "New guests" },
  { value: "cancelled_orders", label: "Cancelled orders" },

];

const currency = (value) => Number(value || 0).toLocaleString("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 });
const allowedLookbackDays = [7, 30, 60, 90, 360];
const allowedSortFields = ["orders", "spent", "joined", "last_active", "name", "role"];
const serverSortFields = ["orders", "spent", "joined", "last_active", "name"];
const positivePage = (value) => Math.max(1, Number.parseInt(value, 10) || 1);
const userSelectionIds = (user) => [...new Set(
  (Array.isArray(user?.selectionIds) ? user.selectionIds.flat(Infinity) : [user?.id])
    .map((id) => String(id || ""))
    .filter(Boolean)
)];
const isUserSelected = (user, selected) => {
  const ids = userSelectionIds(user);
  return ids.length > 0 && ids.every((id) => selected.has(id));
};
const userRecipient = (user, id) => ({ id, name: user.name, detail: user.email });

export const MarketingBuyersSection = ({ customerType = "registered" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useAdminAuth();
  const isGuestDirectory = customerType === "guest";
  const audienceType = isGuestDirectory ? "guest_users" : "registered_users";
  const directoryIcon = isGuestDirectory ? guestUsersIcon : registeredUsersIcon;
  const directorySegments = isGuestDirectory ? guestUserSegments : registeredUserSegments;
  const requestedSegment = searchParams.get("segment") || "all";
  const segment = directorySegments.some((item) => item.value === requestedSegment) ? requestedSegment : "all";
  const requestedDays = searchParams.get("days");
  const requestedNumericDays = Number(requestedDays);
  const days = requestedDays === "allTime" ? "allTime" : allowedLookbackDays.includes(requestedNumericDays) ? requestedNumericDays : 30;
  const requestedSortBy = searchParams.get("sortBy") || "orders";
  const sortBy = allowedSortFields.includes(requestedSortBy) ? requestedSortBy : "orders";
  const sortOrder = searchParams.get("order") === "asc" ? "asc" : "desc";
  const query = searchParams.get("query") || "";
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const page = positivePage(searchParams.get("page"));
  const [users, setUsers] = useState([]);
  const pageSize = 10;
  const [pagination, setPagination] = useState({ total_items: 0, current_page: 1, total_pages: 1, pageSize });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef(null);
  const [selected, setSelected] = useState(() => {
    const stored = getAdminDirectorySelection(audienceType);
    return new Set(stored.ids);
  });
  const [selectedRecords, setSelectedRecords] = useState(() => {
    const stored = getAdminDirectorySelection(audienceType);
    return new Map(stored.recipients.map((recipient) => [recipient.id, recipient]));
  });
  const [selectionPrompt, setSelectionPrompt] = useState(null);
  const canMessage = ADMIN_PERMISSIONS.MARKETING.includes(role);
  const directoryUsers = useMemo(() => {
    if (sortBy !== "role") return users;
    const multiplier = sortOrder === "asc" ? 1 : -1;
    return [...users].sort((first, second) => String(first.type || "").localeCompare(String(second.type || "")) * multiplier);
  }, [sortBy, sortOrder, users]);
  const activeSegment = directorySegments.find((item) => item.value === segment) || directorySegments[0];

  useEffect(() => {
    setLoadError("");
    const stored = getAdminDirectorySelection(audienceType);
    setSelected(new Set(stored.ids));
    setSelectedRecords(new Map(stored.recipients.map((recipient) => [recipient.id, recipient])));
    setSelectionPrompt(null);
    setFiltersOpen(false);
  }, [audienceType, customerType]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("page", String(page));
      next.set("segment", segment);
      next.set("days", String(days));
      next.set("sortBy", sortBy);
      next.set("order", sortOrder);
      return next;
    }, { replace: true });
  }, [days, isGuestDirectory, page, segment, setSearchParams, sortBy, sortOrder]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const closeFilters = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !filtersRef.current?.contains(event.target))) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeFilters);
    document.addEventListener("keydown", closeFilters);
    return () => {
      document.removeEventListener("pointerdown", closeFilters);
      document.removeEventListener("keydown", closeFilters);
    };
  }, [filtersOpen]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");

    const requestSortBy = serverSortFields.includes(sortBy) ? sortBy : "orders";
    const requestOptions = { segment, days, page, pageSize, sortBy: requestSortBy, order: sortOrder, search: debouncedQuery, forceRefresh: true };
    const request = isGuestDirectory ? getGuestUsers(requestOptions) : getRegisteredUsers(requestOptions);

    request
      .then((response) => {
        if (!active) return;
        setUsers(response.data);
        setPagination(response.pagination);
      })
      .catch((error) => {
        if (!active) return;
        setUsers([]);
        setLoadError(error?.response?.data?.message || error?.message || `${isGuestDirectory ? "Guest" : "Registered"} users could not be loaded.`);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [days, debouncedQuery, isGuestDirectory, page, retryKey, segment, sortBy, sortOrder]);

  const visible = directoryUsers;

  const totalItems = pagination.total_items;
  const currentPage = pagination.current_page;
  const totalPages = pagination.total_pages;
  const rangeStart = totalItems ? ((currentPage - 1) * pagination.pageSize) + 1 : 0;
  const rangeEnd = Math.min(currentPage * pagination.pageSize, totalItems);
  const selectableVisible = visible.filter((user) => userSelectionIds(user).length > 0);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((user) => isUserSelected(user, selected));

  const toggleUser = (user) => {
    const ids = userSelectionIds(user);
    if (!ids.length) return;
    const remove = ids.every((id) => selected.has(id));
    const nextSelected = new Set(selected);
    const nextRecords = new Map(selectedRecords);
    ids.forEach((id) => {
      if (remove) {
        nextSelected.delete(id);
        nextRecords.delete(id);
      } else {
        nextSelected.add(id);
        nextRecords.set(id, userRecipient(user, id));
      }
    });
    setSelected(nextSelected);
    setSelectedRecords(nextRecords);
    saveAdminDirectorySelection(audienceType, [...nextSelected], [...nextRecords.values()]);
  };

  const toggleVisible = () => {
    const nextSelected = new Set(selected);
    const nextRecords = new Map(selectedRecords);
    selectableVisible.forEach((user) => userSelectionIds(user).forEach((id) => {
      if (allVisibleSelected) {
        nextSelected.delete(id);
        nextRecords.delete(id);
      } else {
        nextSelected.add(id);
        nextRecords.set(id, userRecipient(user, id));
      }
    }));
    setSelected(nextSelected);
    setSelectedRecords(nextRecords);
    saveAdminDirectorySelection(audienceType, [...nextSelected], [...nextRecords.values()]);
  };

  const updateDirectoryParams = (changes, replace = false) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  };

  const changeServerFilter = (parameter) => (event) => updateDirectoryParams({ [parameter]: event.target.value, page: 1 });

  const changeSort = (value) => {
    const order = sortBy === value ? (sortOrder === "desc" ? "asc" : "desc") : "desc";
    updateDirectoryParams({ sortBy: value, order, page: 1 });
  };

  const SortableHeader = ({ label, value }) => {
    const active = sortBy === value;
    const direction = active ? sortOrder : "none";
    return <th aria-sort={active ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button className={`table-sort ${active ? "active" : ""}`} type="button" onClick={() => changeSort(value)} aria-label={`Sort by ${label}, ${active && sortOrder === "desc" ? "ascending" : "descending"}`}><span>{label}</span><FiArrowUp className={`sort-direction ${direction}`} aria-hidden="true" /></button></th>;
  };

  const openBroadcast = () => {
    if (!selected.size) {
      navigate("/admin/internal/marketing/broadcast", { state: { broadcast: { audience: audienceType, target: "segment", segment, days } } });
      return;
    }

    const stored = getAdminBroadcastSelection();
    const newIds = [...selected].filter((id) => stored.audience !== audienceType || !stored.ids.includes(id));
    if (stored.ids.length && stored.audience === audienceType && newIds.length) {
      setSelectionPrompt({ title: "Add to custom selection?", description: `You already have ${stored.ids.length.toLocaleString()} ${isGuestDirectory ? "guest" : "registered"} user${stored.ids.length === 1 ? "" : "s"} selected. Add ${newIds.length.toLocaleString()} more?`, confirmLabel: "Add and continue" });
      return;
    }
    if (stored.ids.length && stored.audience !== audienceType) {
      setSelectionPrompt({ title: "Replace custom selection?", description: `Your saved selection contains ${stored.ids.length.toLocaleString()} recipients from another directory. A broadcast can target only one audience type at a time.`, confirmLabel: "Replace and continue" });
      return;
    }
    continueToBroadcast();
  };

  const continueToBroadcast = () => {
    const selection = saveAdminBroadcastSelection(audienceType, [...selected], [...selectedRecords.values()]);
    setSelectionPrompt(null);
    navigate("/admin/internal/marketing/broadcast", { state: { broadcast: { ...selection, target: "custom_selection" } } });
  };

  return (
    <>
      <DashboardHeader
        title={isGuestDirectory ? "Guest Users" : "Registered Users"}
        description={isGuestDirectory
          ? "Review customers who checked out without creating a registered account."
          : canMessage ? "Find registered users by engagement, select recipients, and prepare a targeted marketing campaign." : "Track registered users by order frequency and review their customer, transaction and Split-Buy activity."}
        showActions={false}
      />

      <section className="buyers-card">
        <header className="buyers-toolbar">
          <div><h2>{activeSegment.label}</h2><p>{canMessage ? selected.size ? `${selected.size.toLocaleString()} recipient${selected.size === 1 ? "" : "s"} selected across pages.` : "Select individual recipients or broadcast to this complete segment." : "Open any user to review their operational history."}</p></div>
          <div className="buyer-actions">
            <div className="filter-menu" ref={filtersRef}>
              <button className={`filter-trigger ${filtersOpen ? "open" : ""}`} type="button" onClick={() => setFiltersOpen((current) => !current)} aria-expanded={filtersOpen} aria-controls="user-directory-filters">
                <img src={filterIcon} alt="" />
                <span>Filters</span>
                <FiChevronDown aria-hidden="true" />
              </button>
              {filtersOpen && <div className="filter-popup" id="user-directory-filters">
                <header><div><strong>Filter users</strong><span>Refine the directory results</span></div><button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><FiX /></button></header>
                <div className="filter-fields">
                  <label className="audience-filter"><span>Audience</span><select value={segment} onChange={changeServerFilter("segment")} aria-label={`Filter ${isGuestDirectory ? "guest" : "registered"} users by segment`}>{directorySegments.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label>
                  <label className="audience-filter compact-filter"><span>Lookback</span><select value={days} onChange={changeServerFilter("days")} aria-label="Lookback period"><option value={7}>7 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option><option value={360}>360 days</option><option value="allTime">All time</option></select></label>
                </div>
                <button className="filter-done" type="button" onClick={() => setFiltersOpen(false)}>Done</button>
              </div>}
            </div>
            <label className="buyer-search"><FiSearch aria-hidden="true" /><input value={query} onChange={(event) => updateDirectoryParams({ query: event.target.value, page: 1 }, true)} placeholder="Search users" aria-label="Search users" /></label>
            {canMessage && totalItems > 0 && <button className="message-button" type="button" onClick={openBroadcast}><img src={pushChannelIcon} alt="" />{selected.size ? `Broadcast to selected (${selected.size})` : `Broadcast to ${activeSegment.label}`}</button>}
          </div>
        </header>

        {loading ? <div className="directory-loader"><ComponentLoader /></div> : (
          <div className="buyers-table-wrap">
            <table className="buyers-table">
              <thead><tr>{canMessage && <th className="select-cell"><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} aria-label="Select all users shown on this page" /></th>}<SortableHeader label="User" value="name" /><th>Phone</th><SortableHeader label="Role" value="role" /><SortableHeader label="Orders" value="orders" /><SortableHeader label="Amount spent" value="spent" /><SortableHeader label="Last active" value="last_active" /><th>Last order</th><th>Push</th><th aria-label="Actions" /></tr></thead>
              <tbody>{loadError ? <tr className="empty-directory-row"><td colSpan={canMessage ? 10 : 9}><img src={directoryIcon} alt="" /><strong>{isGuestDirectory ? "Guest" : "Registered"} users could not be loaded</strong><span>{loadError}</span><button type="button" className="retry-button" onClick={() => setRetryKey((current) => current + 1)}>Try again</button></td></tr> : visible.length ? visible.map((user, index) => { const selectable = userSelectionIds(user).length > 0; const checked = isUserSelected(user, selected); return <tr key={user.id || `${user.email}-${index}`} className={checked ? "selected" : ""}>{canMessage && <td className="select-cell" data-label="Select"><input type="checkbox" checked={checked} disabled={!selectable} onChange={() => toggleUser(user)} aria-label={selectable ? `Select ${user.name}` : `${user.name} cannot be selected because an ID was not provided`} /></td>}<td className="buyer-cell" data-label="User"><div className="buyer-person"><span className="buyer-avatar" aria-hidden="true">{buyerInitials(user.name)}</span><span><strong>{user.name}</strong><small>{user.email}</small></span></div></td><td data-label="Phone">{user.phone}</td><td data-label="Role"><span className="type-pill">{user.type}</span></td><td data-label="Orders"><strong className="order-count">{user.orders}</strong></td><td data-label="Amount spent"><strong className="order-count">{currency(user.totalSpend)}</strong></td><td data-label="Last active">{user.lastPurchase}</td><td data-label="Last order">{user.lastOrderDate}</td><td data-label="Push"><span className={`availability-pill ${user.hasPushToken ? "available" : "unavailable"}`}>{user.hasPushToken ? "Enabled" : "Unavailable"}</span></td><td className="details-cell"><Link to={`/admin/internal/customers/${isGuestDirectory ? "guests" : "registered"}/${user.id}`}>View details</Link></td></tr>; }) : <tr className="empty-directory-row"><td colSpan={canMessage ? 10 : 9}><img src={directoryIcon} alt="" /><strong>No {isGuestDirectory ? "guest" : "registered"} users match this view</strong><span>Try changing the segment, lookback window, or search term.</span></td></tr>}</tbody>
            </table>
          </div>
        )}

        <footer className="buyers-pagination"><span>{totalItems ? `Showing ${rangeStart}–${rangeEnd} of ${totalItems.toLocaleString()} users` : "No customer records available"}</span><div><button type="button" disabled={loading || currentPage <= 1} onClick={() => updateDirectoryParams({ page: 1 })} aria-label="Go to first page" title="First page"><FiChevronsLeft /></button><button type="button" disabled={loading || currentPage <= 1} onClick={() => updateDirectoryParams({ page: currentPage - 1 })} aria-label="Previous page" title="Previous page"><FiChevronLeft /></button><span>Page {currentPage} of {totalPages}</span><button type="button" disabled={loading || currentPage >= totalPages} onClick={() => updateDirectoryParams({ page: currentPage + 1 })} aria-label="Next page" title="Next page"><FiChevronRight /></button><button type="button" disabled={loading || currentPage >= totalPages} onClick={() => updateDirectoryParams({ page: totalPages })} aria-label="Go to last page" title="Last page"><FiChevronsRight /></button></div></footer>
      </section>
      <p className="data-note"><img src={directoryIcon} alt="" /> {isGuestDirectory ? `Guest users are fetched separately using ${days === "allTime" ? "all-time" : `a ${days}-day`} lookback. Their profiles open in the shared customer details page.` : `Registered users are fetched from the marketing audience service using ${days === "allTime" ? "an all-time" : `a ${days}-day`} lookback.`}</p>
      <SelectionConfirmation prompt={selectionPrompt} onCancel={() => setSelectionPrompt(null)} onConfirm={continueToBroadcast} />
    </>
  );
};
